import {
  Building2,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Loader2,
  Percent,
  Search,
  Sliders,
  Users,
  ShieldCheck,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { api } from '@/lib/api';

import { TenantDetailDrillDown } from './TenantDetailDrillDown';

interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  phone: string | null;
  email: string;
  createdAt: string;
  subscriptionTier: string;
  isActive: boolean;
  maxUsersAllowed: number;
  subscriptionEnd: string | null;
  userCount: number;
  ownerName: string;
  featureFlags: {
    bulkMatrix: boolean;
    gstComplianceExport: boolean;
    loyaltyEngine: boolean;
  };
}

interface PlatformMetrics {
  mrr: number;
  arr: number;
  totalActiveShops: number;
  activeUsersCount: number;
  churnRate: number;
  planDistribution: {
    Silver: number;
    Gold: number;
    Platinum: number;
    Free: number;
  };
}

export function SuperAdminDashboard() {
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [error, setError] = useState('');

  // Search & Pagination States
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);

  // Drilldown selection state
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  // Onboarding Form States
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [plans, setPlans] = useState<any[]>([]);
  const [onboardForm, setOnboardForm] = useState({
    name: '',
    slug: '',
    email: '',
    phone: '',
    owner_name: '',
    plan_tier: 'Silver' as 'Silver' | 'Gold' | 'Platinum' | 'Custom',
    subscription_plan_id: '',
    max_users_allowed: 5,
    admin_email: '',
    admin_password: '',
  });
  const [onboardSaving, setOnboardSaving] = useState(false);
  const [onboardSuccess, setOnboardSuccess] = useState('');

  const fetchPlans = async () => {
    try {
      const res = await api.get('/super/plans');
      if (res.data && res.data.success) {
        setPlans(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching plans', err);
    }
  };

  const handleOnboardFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setOnboardForm((prev) => {
      const updated = { ...prev, [name]: value };

      // Auto-generate slug from Shop Name
      if (name === 'name') {
        updated.slug = value
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .trim()
          .replace(/\s+/g, '-');
      }

      // Auto-update max users allowed based on plan choice
      if (name === 'subscription_plan_id') {
        const selectedPlan = plans.find((p) => p.id === value);
        if (selectedPlan) {
          updated.max_users_allowed = selectedPlan.maxUsers;
        }
      } else if (name === 'plan_tier' && value !== 'Custom') {
        if (value === 'Silver') updated.max_users_allowed = 5;
        else if (value === 'Gold') updated.max_users_allowed = 15;
        else if (value === 'Platinum') updated.max_users_allowed = 999;
      }
      return updated;
    });
  };

  const handleOnboardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOnboardSaving(true);
    setError('');
    setOnboardSuccess('');

    try {
      const payload = {
        ...onboardForm,
        subscription_plan_id: onboardForm.subscription_plan_id || undefined,
        plan_tier: onboardForm.subscription_plan_id
          ? undefined
          : onboardForm.plan_tier === 'Custom'
            ? undefined
            : onboardForm.plan_tier,
        max_users_allowed: Number(onboardForm.max_users_allowed),
      };

      await api.post('/super/tenants/onboard', payload);
      setOnboardSuccess('Tenant environment and administrator account provisioned successfully!');

      // Reset form
      setOnboardForm({
        name: '',
        slug: '',
        email: '',
        phone: '',
        owner_name: '',
        plan_tier: 'Silver',
        subscription_plan_id: '',
        max_users_allowed: 5,
        admin_email: '',
        admin_password: '',
      });

      // Refresh list
      fetchMetrics();
      fetchTenantsList();

      setTimeout(() => {
        setOnboardSuccess('');
        setOnboardOpen(false);
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to onboard new tenant.');
    } finally {
      setOnboardSaving(false);
    }
  };

  const fetchMetrics = async () => {
    setLoadingMetrics(true);
    try {
      const response = await api.get('/super/platform-metrics');
      setMetrics(response.data.data);
    } catch (_err: any) {
      setError('Failed to fetch platform metrics.');
    } finally {
      setLoadingMetrics(false);
    }
  };

  const fetchTenantsList = async () => {
    setLoadingTenants(true);
    try {
      const response = await api.get('/super/tenants', {
        params: {
          page,
          limit,
          search: search.trim() || undefined,
        },
      });
      const { tenants, pagination } = response.data.data;
      setTenants(tenants);
      setTotalPages(pagination.totalPages);
    } catch (_err: any) {
      setError('Failed to fetch tenants list.');
    } finally {
      setLoadingTenants(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    fetchPlans();
  }, []);

  useEffect(() => {
    fetchTenantsList();
  }, [page, search]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchTenantsList();
  };

  // Helper: compute subscription days left
  const getDaysCountdown = (endStr: string | null) => {
    if (!endStr) {
      return { text: 'N/A', class: 'bg-slate-100 text-slate-650 border-slate-205' };
    }
    const end = new Date(endStr).getTime();
    const diff = end - Date.now();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (days < 0) {
      return {
        text: `Expired (${Math.abs(days)}d ago)`,
        class: 'bg-rose-50 text-rose-700 border-rose-100',
      };
    }
    if (days <= 7) {
      return {
        text: `${days} days left`,
        class: 'bg-amber-50 text-amber-700 border-amber-250 animate-pulse',
      };
    }
    return {
      text: `${days} days left`,
      class: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    };
  };

  // Helper: map internal tier names to display values
  const getTierDisplay = (tier: string) => {
    const mapping: Record<string, { label: string; class: string }> = {
      STARTER: { label: 'Silver (Starter)', class: 'bg-blue-50 text-blue-700 border-blue-100' },
      PROFESSIONAL: {
        label: 'Gold (Professional)',
        class: 'bg-amber-50 text-amber-700 border-amber-200',
      },
      ENTERPRISE: {
        label: 'Platinum (Enterprise)',
        class: 'bg-purple-50 text-purple-700 border-purple-100',
      },
      FREE: { label: 'Free Tier', class: 'bg-slate-100 text-slate-600 border-slate-205' },
    };
    return mapping[tier] || { label: tier, class: 'bg-slate-150 text-slate-700 border-slate-200' };
  };

  if (selectedTenantId) {
    return (
      <TenantDetailDrillDown
        tenantId={selectedTenantId}
        onBack={() => {
          setSelectedTenantId(null);
          fetchMetrics();
          fetchTenantsList();
        }}
      />
    );
  }

  if (onboardOpen) {
    return (
      <div className="space-y-6 text-slate-800 font-sans antialiased">
        {/* Header */}
        <div className="flex items-center space-x-3 border-b border-slate-150 pb-4">
          <button
            type="button"
            onClick={() => setOnboardOpen(false)}
            className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-all cursor-pointer shadow-sm text-slate-600"
          >
            <ChevronLeft size={16} />
          </button>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Onboard New Textile Tenant Shop</h2>
            <p className="text-xs text-slate-500">
              Deploy a new multi-tenant environment and provision its primary manager account.
            </p>
          </div>
        </div>

        {onboardSuccess && (
          <div className="p-4 bg-emerald-50 border border-emerald-250 text-xs text-emerald-800 font-semibold rounded-xl flex items-center gap-2.5">
            <span className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-ping" />
            <span>{onboardSuccess}</span>
          </div>
        )}

        {error && (
          <div className="p-4 bg-rose-50 border border-rose-200 text-xs text-rose-700 font-semibold rounded-xl flex items-center gap-2.5">
            <span className="w-2.5 h-2.5 bg-rose-500 rounded-full" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleOnboardSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Shop Details */}
          <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-3.5 flex items-center gap-2">
              <Building2 size={16} className="text-emerald-600" />
              <span>Shop Properties</span>
            </h3>

            <div className="space-y-3.5 text-xs">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Tenant Shop Name
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="e.g. Royal Cotton Hub"
                  value={onboardForm.name}
                  onChange={handleOnboardFormChange}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-medium"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Workspace Slug
                </label>
                <input
                  type="text"
                  name="slug"
                  required
                  placeholder="e.g. royal-cotton"
                  value={onboardForm.slug}
                  onChange={handleOnboardFormChange}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-mono font-medium"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Shop Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="e.g. billing@royalcotton.com"
                  value={onboardForm.email}
                  onChange={handleOnboardFormChange}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-medium"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Contact Phone
                </label>
                <input
                  type="text"
                  name="phone"
                  placeholder="e.g. +91 98765 43210"
                  value={onboardForm.phone}
                  onChange={handleOnboardFormChange}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-mono"
                />
              </div>
            </div>
          </div>

          {/* Admin Credentials */}
          <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-3.5 flex items-center gap-2">
              <Users size={16} className="text-emerald-600" />
              <span>Owner & Admin Credentials</span>
            </h3>

            <div className="space-y-3.5 text-xs">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Owner Full Name
                </label>
                <input
                  type="text"
                  name="owner_name"
                  required
                  placeholder="e.g. Rajesh Kumar"
                  value={onboardForm.owner_name}
                  onChange={handleOnboardFormChange}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-medium"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Primary Admin Login Email
                </label>
                <input
                  type="email"
                  name="admin_email"
                  required
                  placeholder="e.g. rajesh@royalcotton.com"
                  value={onboardForm.admin_email}
                  onChange={handleOnboardFormChange}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-medium"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Login Password
                </label>
                <input
                  type="password"
                  name="admin_password"
                  required
                  placeholder="••••••••"
                  value={onboardForm.admin_password}
                  onChange={handleOnboardFormChange}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-mono"
                />
              </div>
            </div>
          </div>

          {/* Plan Settings */}
          <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4 flex flex-col justify-between">
            <div className="space-y-4">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-3.5 flex items-center gap-2">
                <Sliders size={16} className="text-emerald-600" />
                <span>Subscription Plan & Constraints</span>
              </h3>

              <div className="space-y-3.5 text-xs">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Plan Package Tier
                  </label>
                  <select
                    name="plan_tier"
                    value={
                      onboardForm.subscription_plan_id
                        ? `custom-${onboardForm.subscription_plan_id}`
                        : onboardForm.plan_tier
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val.startsWith('custom-')) {
                        const planId = val.replace('custom-', '');
                        setOnboardForm((prev) => {
                          const updated = {
                            ...prev,
                            plan_tier: 'Custom' as any,
                            subscription_plan_id: planId,
                          };
                          const selectedPlan = plans.find((p) => p.id === planId);
                          if (selectedPlan) {
                            updated.max_users_allowed = selectedPlan.maxUsers;
                          }
                          return updated;
                        });
                      } else {
                        setOnboardForm((prev) => {
                          const updated = {
                            ...prev,
                            plan_tier: val as any,
                            subscription_plan_id: '',
                          };
                          if (val === 'Silver') updated.max_users_allowed = 5;
                          else if (val === 'Gold') updated.max_users_allowed = 15;
                          else if (val === 'Platinum') updated.max_users_allowed = 999;
                          return updated;
                        });
                      }
                    }}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-bold cursor-pointer bg-white"
                  >
                    <optgroup label="Standard Presets">
                      <option value="Silver">Silver (Starter)</option>
                      <option value="Gold">Gold (Professional)</option>
                      <option value="Platinum">Platinum (Enterprise)</option>
                    </optgroup>
                    {plans.length > 0 && (
                      <optgroup label="Dynamic Database Plans">
                        {plans.map((p: any) => (
                          <option key={p.id} value={`custom-${p.id}`}>
                            {p.name} (₹{p.price}/mo)
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    User Capacity limit
                  </label>
                  <input
                    type="number"
                    name="max_users_allowed"
                    required
                    min="1"
                    value={onboardForm.max_users_allowed}
                    onChange={handleOnboardFormChange}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-mono font-bold"
                  />
                  <p className="text-[10px] text-slate-400">
                    Defaults automatically: Silver (5), Gold (15), Platinum (999).
                  </p>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={onboardSaving}
              className="w-full py-3 mt-4 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5 cursor-pointer shadow-md transition-all"
            >
              {onboardSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Deploying Tenant Workspace...</span>
                </>
              ) : (
                <>
                  <ShieldCheck size={16} />
                  <span>Deploy & Provision Instance</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6 font-sans antialiased text-slate-800">
      {/* Premium Gradient Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-800 via-emerald-750 to-teal-900 p-6 sm:p-8 text-white shadow-lg border border-emerald-700/30">
        <div className="absolute right-0 top-0 -mr-16 -mt-16 w-64 h-64 bg-emerald-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/20 text-xs font-semibold text-emerald-250 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
              SaaS Control Panel
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              SaaS Platform Overview
            </h1>
            <p className="mt-1.5 text-sm text-emerald-150 max-w-2xl font-light">
              Secure Product Owner Console. Manage active tenant shop licenses, overrides, feature
              flags, and observe platform financial telemetry.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOnboardOpen(true)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-750 text-white rounded-lg text-xs font-semibold transition-all shadow-sm cursor-pointer border border-emerald-500"
            >
              Onboard Shop
            </button>
            <button
              onClick={() => {
                fetchMetrics();
                fetchTenantsList();
              }}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white border border-white/10 rounded-lg text-xs font-semibold transition-all shadow-sm cursor-pointer"
            >
              Sync Live Matrices
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-xs text-rose-700 font-semibold rounded-xl flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-rose-500" />
          {error}
        </div>
      )}

      {/* Metrics Telemetry Grid */}
      {loadingMetrics ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="card h-28 flex items-center justify-center text-slate-400 bg-white border border-slate-100 rounded-xl shadow-sm"
            >
              <Loader2 className="w-5 h-5 animate-spin mr-2 text-emerald-600" />
              <span className="text-xs">Loading telemetry...</span>
            </div>
          ))}
        </div>
      ) : (
        metrics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* MRR / ARR */}
            <div className="card bg-white border border-slate-100 hover:border-slate-200 p-5 rounded-xl shadow-sm flex justify-between items-start transition-all hover:shadow">
              <div className="space-y-1.5">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                  Platform MRR
                </span>
                <p className="text-2xl font-extrabold text-slate-800 font-mono">
                  ₹{metrics.mrr.toLocaleString()}
                </p>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                  <span className="font-bold text-emerald-600">ARR:</span>
                  <span className="font-mono">₹{metrics.arr.toLocaleString()}</span>
                </div>
              </div>
              <div className="p-2.5 bg-emerald-50 rounded-xl text-emerald-600 border border-emerald-100">
                <DollarSign size={20} />
              </div>
            </div>

            {/* Active shops */}
            <div className="card bg-white border border-slate-100 hover:border-slate-200 p-5 rounded-xl shadow-sm flex justify-between items-start transition-all hover:shadow">
              <div className="space-y-1.5">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                  Active Shop Licenses
                </span>
                <p className="text-2xl font-extrabold text-slate-800">{metrics.totalActiveShops}</p>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span>Online Environments</span>
                </div>
              </div>
              <div className="p-2.5 bg-sky-50 rounded-xl text-sky-650 border border-sky-100">
                <Building2 size={20} />
              </div>
            </div>

            {/* Platform Churn */}
            <div className="card bg-white border border-slate-100 hover:border-slate-200 p-5 rounded-xl shadow-sm flex justify-between items-start transition-all hover:shadow">
              <div className="space-y-1.5">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                  Platform Churn Rate
                </span>
                <p className="text-2xl font-extrabold text-slate-800 font-mono">
                  {metrics.churnRate}%
                </p>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                  <span>Suspended client ratio</span>
                </div>
              </div>
              <div className="p-2.5 bg-rose-50 rounded-xl text-rose-650 border border-rose-100">
                <Percent size={20} />
              </div>
            </div>

            {/* Active plan distribution */}
            <div className="card bg-white border border-slate-100 hover:border-slate-200 p-5 rounded-xl shadow-sm flex justify-between items-start transition-all hover:shadow">
              <div className="space-y-1.5 w-full">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                  Subscription Tiers
                </span>
                <div className="grid grid-cols-3 gap-1 pt-1">
                  <div className="bg-slate-50 p-1.5 rounded text-center border border-slate-100">
                    <div className="text-[9px] text-slate-400 uppercase font-semibold">Silver</div>
                    <div className="text-xs font-extrabold text-slate-700">
                      {metrics.planDistribution.Silver}
                    </div>
                  </div>
                  <div className="bg-amber-50/40 p-1.5 rounded text-center border border-amber-100/50">
                    <div className="text-[9px] text-amber-600 uppercase font-semibold">Gold</div>
                    <div className="text-xs font-extrabold text-amber-700">
                      {metrics.planDistribution.Gold}
                    </div>
                  </div>
                  <div className="bg-purple-50/40 p-1.5 rounded text-center border border-purple-100/50">
                    <div className="text-[9px] text-purple-600 uppercase font-semibold">Plat</div>
                    <div className="text-xs font-extrabold text-purple-700">
                      {metrics.planDistribution.Platinum}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {/* Main Grid: Directory & Configurator Boundary Guidance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Master Tenant Directory Table (Left 2/3) */}
        <div className="lg:col-span-2 card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                <Users size={16} className="text-emerald-600" />
                <span>Tenant Shop Directory</span>
              </h2>
              <p className="text-[11px] text-slate-400">
                Click on any tenant profile to drill down and configuration overrides.
              </p>
            </div>

            <form onSubmit={handleSearchSubmit} className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search shop by name or subdomain..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="block w-full pl-9 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 transition-all"
              />
            </form>
          </div>

          {loadingTenants ? (
            <div className="flex flex-col items-center justify-center py-24 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mb-2" />
              <span className="text-xs font-semibold">Syncing tenant configurations...</span>
            </div>
          ) : tenants.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <Building2 className="mx-auto mb-3 opacity-30 text-emerald-600" size={40} />
              <p className="text-xs font-bold">
                No active or inactive tenants found matching filter.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto border border-slate-100 rounded-lg">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider bg-slate-50/50">
                      <th className="py-2.5 px-3 text-center w-12">S.No.</th>
                      <th className="py-2.5 px-3">Tenant Info</th>
                      <th className="py-2.5 px-2">Contacts</th>
                      <th className="py-2.5 px-2">License Tier</th>
                      <th className="py-2.5 px-2 text-center">User Slots Ratio</th>
                      <th className="py-2.5 px-3 text-center">Expiry Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tenants.map((t, idx) => {
                      const countdown = getDaysCountdown(t.subscriptionEnd);
                      const tier = getTierDisplay(t.subscriptionTier);
                      return (
                        <tr
                          key={t.id}
                          onClick={() => setSelectedTenantId(t.id)}
                          className="hover:bg-slate-50/70 cursor-pointer transition-colors"
                        >
                          <td className="py-3 px-3 text-center text-slate-500 font-medium">
                            {idx + 1}
                          </td>
                          <td className="py-3 px-3">
                            <span className="font-bold text-slate-800 hover:text-emerald-700 block transition-colors">
                              {t.name}
                            </span>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-[10px] text-slate-400 font-mono">{t.slug}</span>
                              <span className="text-[10px] text-slate-350">•</span>
                              <span className="text-[10px] text-slate-500 font-semibold">
                                {t.ownerName || 'No Owner Name'}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <span className="text-[10px] text-slate-500 font-mono block">
                              {t.phone || 'N/A'}
                            </span>
                            <span className="text-[9px] text-slate-400 block truncate max-w-[150px]">
                              {t.email}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold border ${tier.class}`}
                            >
                              {tier.label}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center font-mono">
                            <div className="flex flex-col items-center">
                              <span
                                className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                  t.userCount >= t.maxUsersAllowed
                                    ? 'bg-rose-50 text-rose-700 border border-rose-100'
                                    : 'bg-slate-150 text-slate-700'
                                }`}
                              >
                                {t.userCount} / {t.maxUsersAllowed} Staff
                              </span>
                              <div className="w-16 bg-slate-100 h-1 rounded-full mt-1 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${t.userCount >= t.maxUsersAllowed ? 'bg-rose-500' : 'bg-emerald-500'}`}
                                  style={{
                                    width: `${Math.min((t.userCount / t.maxUsersAllowed) * 100, 100)}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded border ${countdown.class}`}
                            >
                              {countdown.text}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <span className="text-[11px] text-slate-400">
                  Page <span className="font-bold text-slate-750">{page}</span> of{' '}
                  <span className="font-bold text-slate-750">{totalPages}</span>
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                    className="p-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 rounded-lg disabled:opacity-50 cursor-pointer transition-colors"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                    className="p-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 rounded-lg disabled:opacity-50 cursor-pointer transition-colors"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right column containing Guidelines, Telemetry & Audits */}
        <div className="space-y-6">
          {/* Subscription Configurations & Limits Matrix Panel */}
          <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4">
            <div className="border-b border-slate-100 pb-3">
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                <Sliders size={16} className="text-emerald-600" />
                <span>SaaS Gating Guidelines</span>
              </h2>
              <p className="text-[11px] text-slate-400">
                Standardized product bounds for the 3 packages.
              </p>
            </div>

            <div className="space-y-3.5 text-xs">
              {/* Silver Tier Info */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200/60 relative overflow-hidden">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="font-bold text-blue-700">Silver Package</span>
                  <span className="font-bold text-slate-700">₹1,999/mo</span>
                </div>
                <ul className="space-y-1 text-[10px] text-slate-500 font-medium">
                  <li>
                    • Cashier Limit:{' '}
                    <span className="font-bold text-slate-700">5 Max Staff Accounts</span>
                  </li>
                  <li>• POS Invoice creation allowed</li>
                  <li>
                    • Bulk matrix inventory intake:{' '}
                    <span className="text-rose-600 font-bold">Blocked</span>
                  </li>
                  <li>
                    • GST Audits & Export: <span className="text-rose-600 font-bold">Blocked</span>
                  </li>
                  <li>
                    • Customer Loyalty Engine:{' '}
                    <span className="text-rose-600 font-bold">Blocked</span>
                  </li>
                </ul>
              </div>

              {/* Gold Tier Info */}
              <div className="p-3 bg-amber-50/50 rounded-lg border border-amber-200/50 relative overflow-hidden">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="font-bold text-amber-700">Gold Package</span>
                  <span className="font-bold text-slate-700">₹4,999/mo</span>
                </div>
                <ul className="space-y-1 text-[10px] text-slate-500 font-medium">
                  <li>
                    • Cashier Limit:{' '}
                    <span className="font-bold text-slate-700">15 Max Staff Accounts</span>
                  </li>
                  <li>• POS Invoice creation allowed</li>
                  <li>
                    • Bulk matrix inventory intake:{' '}
                    <span className="text-emerald-600 font-bold">Enabled</span>
                  </li>
                  <li>
                    • GST Audits & Export: <span className="text-rose-600 font-bold">Blocked</span>
                  </li>
                  <li>
                    • Customer Loyalty Engine:{' '}
                    <span className="text-rose-600 font-bold">Blocked</span>
                  </li>
                </ul>
              </div>

              {/* Platinum Tier Info */}
              <div className="p-3 bg-purple-50/45 rounded-lg border border-purple-200/40 relative overflow-hidden">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="font-bold text-purple-700">Platinum Package</span>
                  <span className="font-bold text-slate-700">₹9,999/mo</span>
                </div>
                <ul className="space-y-1 text-[10px] text-slate-500 font-medium">
                  <li>
                    • Cashier Limit:{' '}
                    <span className="font-bold text-slate-700">999 (Unlimited) Accounts</span>
                  </li>
                  <li>• POS Invoice creation allowed</li>
                  <li>
                    • Bulk matrix inventory intake:{' '}
                    <span className="text-emerald-600 font-bold">Enabled</span>
                  </li>
                  <li>
                    • GST Audits & Export:{' '}
                    <span className="text-emerald-600 font-bold">Enabled</span>
                  </li>
                  <li>
                    • Customer Loyalty Engine:{' '}
                    <span className="text-emerald-600 font-bold">Enabled</span>
                  </li>
                </ul>
              </div>

              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100 text-[11px] text-emerald-800 flex items-start gap-2">
                <ShieldCheck className="shrink-0 mt-0.5 text-emerald-600" size={15} />
                <p className="leading-relaxed">
                  Rules are automatically enforced at the API Gateway level. Expired subscriptions
                  immediately halt billing, and user registration limits prevent excess Cashier
                  creations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export { SuperAdminDashboard as SaasAdminDashboard };
